import React, { useCallback, useMemo, useState } from 'react';

import { ApolloError, gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments, FilterListPopover, FilterSelectGroup, MaybeTooltip } from '@modernloop/shared/components';
import { PlusIcon } from '@modernloop/shared/icons';
import {
  AutocompleteRenderGroupParams,
  Box,
  Checkbox,
  Chip,
  ListItem,
  ListItemButton,
  ListSubheader,
  Stack,
  Typography,
} from '@mui/material';

import { TaskTagsBase_TaskTagFragment, useTaskTagsQuery } from 'src/generated/mloop-graphql';

import FilterButton from 'src/components/FilterButton';

type Props = {
  loading: boolean;
  error?: ApolloError;
  selectedTaskTags: TaskTagsBase_TaskTagFragment[];
  onChange: (newTaskTags: TaskTagsBase_TaskTagFragment[]) => void;
  getLabel?: () => React.ReactNode;
  hideSelectedTagChips?: boolean;
  onClose?: () => void;
};

type Fragments = {
  taskTags: TaskTagsBase_TaskTagFragment[];
};

const isTaskTagOptionEqualToValue = (option: TaskTagsBase_TaskTagFragment, value: TaskTagsBase_TaskTagFragment) =>
  option.id === value.id;

const TaskTagsBase: FCWithFragments<Fragments, Props> = ({
  loading,
  taskTags,
  selectedTaskTags,
  onChange,
  getLabel,
  hideSelectedTagChips,
  onClose,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const options = useMemo(() => {
    const selectedTaskTagIds = selectedTaskTags.map((taskTag) => taskTag.id);
    return [...taskTags]
      .sort((a, b) => a.name.localeCompare(b.name))
      .sort((a, b) => {
        if (selectedTaskTagIds.includes(a.id) && selectedTaskTagIds.includes(b.id)) {
          return 0;
        }
        if (selectedTaskTagIds.includes(a.id)) {
          return -1;
        }
        return 1;
      });
  }, [selectedTaskTags, taskTags]);

  const groupBy = useCallback(
    (taskTag: TaskTagsBase_TaskTagFragment) => {
      return selectedTaskTags.findIndex((selectedTag) => selectedTag.id === taskTag.id) !== -1
        ? FilterSelectGroup.SELECTED
        : FilterSelectGroup.SUGGESTED;
    },
    [selectedTaskTags]
  );

  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, taskTag: TaskTagsBase_TaskTagFragment) => {
      const checked = selectedTaskTags.findIndex((selectedTag) => selectedTag.id === taskTag.id) !== -1;
      return (
        <ListItem {...props}>
          <ListItemButton>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ minWidth: 0 }}
              data-testid={`entity-task-tags-filter-select-option-${taskTag.id}`}
            >
              <Checkbox checked={checked} sx={{ flexShrink: 0 }} />
              <MaybeTooltip label={taskTag.name} title={taskTag.name} />
            </Stack>
          </ListItemButton>
        </ListItem>
      );
    },
    [selectedTaskTags]
  );

  const renderLabel = useCallback(() => {
    const label = t('hiring-dashboard-filter:filter.addTags');
    return (
      <Stack flexDirection="row" alignItems="center" data-testid="entity-task-tags-filter-button">
        <PlusIcon fontSize="medium" />
        <MaybeTooltip label={label} labelProps={{ color: 'info', variant: 'body2' }} title={label} />
      </Stack>
    );
  }, [t]);

  const handleDelete = useCallback(
    (taskTag: TaskTagsBase_TaskTagFragment) => {
      onChange(selectedTaskTags.filter((selectedTag) => selectedTag.id !== taskTag.id));
    },
    [onChange, selectedTaskTags]
  );

  const renderGroup = useCallback(
    (params: AutocompleteRenderGroupParams) => {
      let groupLabel: React.ReactNode = params.group;
      if (params.group === FilterSelectGroup.SUGGESTED) {
        groupLabel = t('hiring-dashboard-filter:filter.groupSuggestions');
      } else if (params.group === FilterSelectGroup.SELECTED) {
        groupLabel = t('hiring-dashboard-filter:filter.groupSelected');
      } else if (params.group === FilterSelectGroup.OTHER) {
        groupLabel = t('hiring-dashboard-filter:filter.groupOther');
      }
      return [<ListSubheader key={params.key}>{groupLabel}</ListSubheader>, params.children];
    },
    [t]
  );

  const renderFilterSelect = useCallback(() => {
    return (
      <Stack data-testid="entity-task-tags-filter-select">
        <FilterListPopover
          open={open}
          options={options}
          values={selectedTaskTags}
          loading={loading}
          placeholderText={t('hiring-dashboard-filter:filter.searchTags')}
          noOptionsText={t('hiring-dashboard-settings-tasks:settings.tasks.tags.noTagsFound')}
          isOptionEqualToValue={isTaskTagOptionEqualToValue}
          groupBy={groupBy}
          renderGroup={renderGroup}
          getOptionLabel={(taskTag) => taskTag.name}
          renderOption={renderOption}
          onChange={(event, values) => onChange(values)}
          onPopoverClose={() => {
            setOpen(false);
            onClose?.();
          }}
        >
          <FilterButton hasSelections={selectedTaskTags.length > 0} onClick={() => setOpen(true)}>
            {getLabel?.() || renderLabel()}
          </FilterButton>
        </FilterListPopover>
      </Stack>
    );
  }, [
    getLabel,
    open,
    groupBy,
    loading,
    onChange,
    onClose,
    options,
    renderGroup,
    renderLabel,
    renderOption,
    selectedTaskTags,
    t,
  ]);

  return hideSelectedTagChips ? (
    renderFilterSelect()
  ) : (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', width: '100%' }}>
      {renderFilterSelect()}

      {selectedTaskTags.map((taskTag) => {
        return (
          <Chip
            key={taskTag.id}
            data-testid={`entity-task-tags-filter-chip-${taskTag.id}`}
            label={<Typography variant="body2">{taskTag.name}</Typography>}
            onDelete={() => handleDelete(taskTag)}
            sx={{ margin: 0 }}
          />
        );
      })}
    </Box>
  );
};

TaskTagsBase.fragments = {
  taskTags: gql`
    fragment TaskTagsBase_taskTag on TaskTag {
      id
      name
    }
  `,
};

type TaskTagsFragmentProps = {
  selectedTaskTags: TaskTagsBase_TaskTagFragment[];
};

type TaskTagsProps = Pick<Props, 'selectedTaskTags' | 'onChange' | 'getLabel' | 'hideSelectedTagChips' | 'onClose'>;

// eslint-disable-next-line modernloop/restric-fragments-name.cjs
const TaskTags: FCWithFragments<TaskTagsFragmentProps, TaskTagsProps> = ({
  selectedTaskTags,
  getLabel,
  onChange,
  hideSelectedTagChips,
  onClose,
}) => {
  const { data, loading, error } = useTaskTagsQuery();

  return (
    <TaskTagsBase
      loading={loading}
      error={error}
      taskTags={data?.taskTags?.items || []}
      selectedTaskTags={selectedTaskTags}
      onChange={onChange}
      getLabel={getLabel}
      hideSelectedTagChips={hideSelectedTagChips}
      onClose={onClose}
    />
  );
};

TaskTags.fragments = {
  selectedTaskTags: gql`
    ${TaskTagsBase.fragments.taskTags}
    fragment TaskTags_selectedTaskTag on TaskTag {
      ...TaskTagsBase_taskTag
    }
  `,
};

export default TaskTags;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskTagsQuery = gql`
  ${TaskTags.fragments.selectedTaskTags}
  query TaskTags {
    taskTags(input: { page: { offset: 0, limit: 100 } }) {
      items {
        ...TaskTags_selectedTaskTag
      }
    }
  }
`;
